<template>
  <div class="row">
    <div class="col-8">
      <div class="card">
        <div class="card-header">
          <h3 class="card-title">
            Incident Info - {{ displayStatus }}
            <button
              class="btn btn-sm btn-outline-primary"
              @click="openResubmissionModal"
              v-if="incident?.status === 'AwaitingEvidence'"
            >
              Resubmit Incident
            </button>
            <button
              ref="editButton"
              :disabled="!incident?.id"
              v-if="incident?.status !== 'Closed'"
              @click="toggleEdit"
              class="btn btn-sm float-right"
              :class="{
                'btn-outline-primary': !editing,
                'btn-outline-danger': editing,
              }"
            >
              {{ editing ? "Cancel Edit" : "Edit Incident" }}
            </button>
            <button
              v-show="editing"
              :disabled="!incident?.id || saving"
              class="btn btn-sm btn-primary float-right m-r-sm"
              @click="saveChanges"
            >
              Save
            </button>
            <button
              v-show="!editing"
              :disabled="!incident?.id"
              v-if="
                incident?.status !== 'Closed' && incident?.status !== 'Rejected'
              "
              class="btn btn-sm btn-outline-danger float-right m-r-sm"
              @click="closure.modal.show()"
            >
              Request to Close
            </button>
          </h3>
        </div>
        <div 
          v-if="showAlertsBanner" 
          class="alert alert-info m-0 border-top-0 rounded-0 d-flex align-items-center"
        >
          <i class="material-icons me-2">notifications_active</i>
          <div>
            <strong>Alert Notification:</strong> This incident is currently being processed. You will start receiving alerts locally when people matching this incident are detected.
          </div>
        </div>
        <div class="card-body" v-if="incident?.id">
          <div
            v-show="editing"
            class="alert alert-custom alert-indicator-left indicator-warning mg-b-sm"
            role="alert"
          >
            <div class="alert-content">
              <span class="alert-title">Warning!</span>
              <span class="alert-text"
                >Any changes you make to the incident will be recorded for
                auditing purposes.</span
              >
            </div>
          </div>

          <div class="row">
            <div class="col-6">
              <div class="form-group">
                <label for="incidentType">Incident Severity</label>
                  <p>
                    <span :class="`sev-${severity.toLowerCase().replace(' ', '-')}`"></span>
                    {{ severity.toUpperCase() }}
                  </p>
              </div>
            </div>
            <div class="col-6">
              <div class="form-group">
                <label for="incidentType">Incident Type</label>
                <div v-show="!editing">
                  <p>
                    <span
                      v-for="type in types"
                      class="badge badge-warning m-r-xs"
                      >{{ type.name }}</span
                    >
                  </p>
                </div>
                <div v-show="editing">
                  <Multiselect
                    :class="{ 'multi-dark': $store.state.auth.status.dark }"
                    mode="tags"
                    placeholder="Select types"
                    :close-on-select="false"
                    :searchable="true"
                    :create-option="true"
                    :multiple="true"
                    :options="availableTypes"
                    label="name"
                    track-by="type_id"
                    name="types"
                    class="m-b-sm"
                    v-model="types"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-6">
              <div class="form-group">
                <label for="incidentType">Occurred</label>
                <div>
                  <p>{{ incident_date }} : {{ incident_time }}</p>
                </div>
              </div>
            </div>
            <div class="col-6">
              <div class="form-group">
                <label for="incidentType">Reported</label>
                <p>{{ created_date }} : {{ created_time }}</p>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-6">
              <div class="form-group">
                <label>Reported By</label>
                <p>{{ incident.created_by_user || "N/A" }}</p>
              </div>
            </div>
            <div class="col-6">
              <div class="form-group">
                <label>Police Force</label>
                <div v-show="!editing">
                  <p>{{ incident.police_force }}</p>
                </div>
                <div v-show="editing">
                  <input
                    type="text"
                    class="form-control m-b-sm"
                    v-model="incident.police_force"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-6">
              <div class="form-group">
                <label>Crime Reference</label>
                <div v-show="!editing">
                  <p>{{ incident.police_ref || "-" }}</p>
                </div>
                <div v-show="editing">
                  <input
                    type="text"
                    class="form-control m-b-sm"
                    v-model="incident.police_ref"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <div class="form-group">
                <label for="incidentType">Description</label>
                <div v-if="!editing || incident.status === 'Approved'">
                  <p>
                    {{ incident.description || "No description provided" }}
                  </p>
                </div>
                <div v-show="editing && incident.status !== 'Approved'">
                  <textarea
                    class="form-control m-b-sm"
                    rows="5"
                    v-model="incident.description"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <div class="form-group">
                <p style="font-style: italic;">Any additonal information you wish to include can be put into Incident Notes. Please 
                  <span 
                    style="font-weight: bold; cursor: pointer;" 
                    @click="scrollToIncidentNotes"
                  >scroll down</span>
                 to see this section.</p>
              </div>
            </div>
          </div>
        </div>
        <div class="card-body" v-else>
          <div class="wrapper">
            <div class="sub-wrapper">
              <Loader />
            </div>
          </div>
        </div>
      </div>

      <div class="card m-t-sm">
        <div class="card-header">
          <h3 class="card-title">Witnesses</h3>
        </div>
        <div class="card-body">
          <div class="table-responsive">
          <table class="table">
            <thead>
              <tr>
                <th>Name <span class="text-danger">*</span></th>
                <th>Type <span class="text-danger">*</span></th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="witness in witnesses">
                <td>{{ witness.witness }}</td>
                <td>{{ witness.type }}</td>
                <td>{{ witness.notes }}</td>
                <td>
                  <button
                    :disabled="witness.submitting"
                    class="btn btn-sm btn-danger"
                    @click="removeWitness(witness)"
                  >
                    Remove
                  </button>
                </td>
              </tr>
              <tr>
                <td>
                  <input
                    :disabled="witness.submitting"
                    v-model="witness.name"
                    type="text"
                    class="form-control"
                    placeholder="Name"
                    required
                  />
                </td>
                <td>
                  <select
                    :disabled="witness.submitting"
                    v-model="witness.type"
                    class="form-control"
                    required
                  >  
                    <option></option>
                    <option>Staff</option>
                    <option>Public</option>
                    <option>Emergency Services</option>
                  </select>
                </td>
                <td>
                  <textarea
                    :disabled="witness.submitting"
                    v-model="witness.notes"
                    class="form-control"
                    rows="2"
                    placeholder="Notes"
                  ></textarea>
                </td>
                <td>
                  <button
                    :disabled="!witness.name || !witness.type"
                    class="btn btn-sm btn-primary"
                    @click="addWitness"
                  >
                    Add
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>
      </div>

      <div class="card m-t-sm">
        <div class="card-header">
          <h3 class="card-title">
            Evidence
            <button
              :disabled="!incident?.id"
              class="btn btn-sm btn-outline-primary float-right"
              @click="awaitingEvidenceCheck()"
            >
              Upload Evidence
            </button>
          </h3>
        </div>
        
        <div v-if="missingEvidence.length > 0" class="alert alert-warning m-3 mb-0">
          <i class="material-icons me-2 align-middle">warning</i>
          <strong>Missing Evidence!</strong> {{ missingEvidence.length }} {{ missingEvidence.length === 1 ? 'file' : 'files' }} need to be uploaded.
          <button class="btn btn-sm btn-warning ms-3" @click="scrollToMissingEvidence">View Missing Files</button>
        </div>
  
        <div v-if="uploadProgress.status !== 'idle'" class="card mb-3">
          <div class="card-header">
            <h3 class="card-title">
              Evidence Upload Progress
              <span class="badge bg-primary ms-2">
                {{ uploadProgress.completed }} / {{ uploadProgress.total }}
              </span>
            </h3>
          </div>
          <div class="card-body">
            <div class="alert alert-warning mb-3" v-if="uploadProgress.status === 'uploading'">
              <i class="material-icons me-2 align-middle">warning</i>
              <strong>Upload in progress!</strong> Please don't close this tab until all files are uploaded.
            </div>
            
            <div class="alert alert-success mb-3" v-if="uploadProgress.status === 'complete'">
              <i class="material-icons me-2 align-middle">check_circle</i>
              <strong>Upload complete!</strong> All files have been successfully uploaded.
            </div>
            
            <div class="progress mb-3">
              <div 
                class="progress-bar" 
                role="progressbar" 
                :style="`width: ${uploadProgress.overallProgress || 0}%`" 
                :class="{
                  'bg-success': uploadProgress.status === 'complete',
                  'progress-bar-striped progress-bar-animated': uploadProgress.status === 'uploading'
                }"
                :key="`progress-${Date.now()}-${uploadProgress.overallProgress}`"
              >
                {{ uploadProgress.overallProgress || 0 }}%
              </div>
            </div>
          </div>
        </div>
        <div class="card-body">
          <div class="row">
            <div 
              class="col-4 m-b-sm" 
              v-for="file in evidence"
              @mouseover="hoveredFileId = file.id" 
              @mouseleave="hoveredFileId = null"
              :id="(file.url === '' || file.url === null) ? 'missing-evidence-' + file.id : ''"
            >
              <div>              
                <div 
                  style="position: relative;" 
                  class="evidence-item"
                  :class="{'needs-reupload': file.url === '' || file.url === null}"
                  @click="(file.url === '' || file.url === null) && !uploadInProgress ? getReuploadLink(file.id) : null"
                >
                  <span class="file-type-badge">{{ file.filename.split('.').pop().toUpperCase() }}</span>
                  
                  <div v-if="file.url === '' || file.url === null" class="needs-upload-badge">
                    <i class="material-icons me-1">error</i>
                    Needs Upload
                  </div>

                  <div 
                    v-if="(file.url === '' || file.url === null) && hoveredFileId === file.id && !uploadInProgress"
                    class="centered-upload-icon"
                  >
                    <i class="material-icons">upload</i>
                  </div>
                  
                  <img
                    v-viewer
                    :src="file.url"
                    v-if="file.filetype === 'image'"
                    alt="evidence"
                    class="img-fluid"
                  />

                  <video
                    :src="file.url"
                    v-else-if="file.filetype === 'video'"
                    controls
                    class="img-fluid"
                  ></video>
                  
                  <a target="_blank" 
                    :href="file.url" 
                    v-else 
                  >{{ file.filename }}</a>

                </div>
                <div class="mt-2">
                  <small class="text-muted d-block">{{ file.description || "No description" }}</small>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
      <div class="card m-t-sm" ref="incidentNotes">
        <div class="card-header">
          <h3 class="card-title">Incident Notes</h3>
        </div>
        <div class="card-body" v-if="this.incidentNotes.length > 0" style="max-height: 350px; overflow-y: auto;">
          <div v-for="(note) in incidentNotes" 
            :key="note.id + 'note'" 
            class="note-item" 
            :class="{'own': note.user_id === $store.state.auth.user.id}"
            @mouseover="hoveredNote = note.id"
            @mouseleave="hoveredNote = null"
          >
            <div class="note-header">
              <strong>{{ note.email }}</strong>
              <span>{{ note.created_date }} : {{ note.created_time }}</span>
              <span v-if="note.user_id === $store.state.auth.user.id && hoveredNote === note.id" class="edit-icon" @click="toggleNoteEdit(note)">
                <span class="material-icons">edit</span>
              </span>
            </div>
            <div v-if="editedNoteId === note.id" style="position: relative;">
              <textarea v-model="editedNote" class="form-control" rows="3"></textarea>
              <div style="display: flex; justify-content: space-between; margin-top: 10px;">
                <button @click="saveEdit(note)" class="btn btn-sm btn-success" style="flex: 1; margin-right: 5px;">
                  <span class="material-icons">check</span>
                </button>
                <button @click="toggleNoteEdit(note)" class="btn btn-sm btn-danger" style="flex: 1; margin-left: 5px;">
                  <span class="material-icons">close</span>
                </button>
              </div>
            </div>
            <div v-else class="note-content">{{ note.note }}</div>
          </div>
        </div>
        <div v-else class="no-notes">
          No notes added.
        </div>
        <div class="card-footer d-flex">
          <textarea v-model="newNote" class="form-control me-2" rows="1" placeholder="Add a note..."></textarea>
          <button @click="addNote" class="btn btn-dark d-flex align-items-center">
            <span class="material-icons ms-1">send</span>
          </button>
        </div>
      </div>
    </div>

    <div class="col-4">
      <div class="card">
        <div class="card-header">
          <h3 class="card-title">Person of Interest</h3>
          <span v-if="otherFaces?.length > 0" class="badge bg-info ms-2">
            {{ otherFaces.length + 1 }} faces
          </span>
        </div>
        <div v-if="incident?.id !== undefined" class="card-body">
          <div v-if="allFaces.length > 1" class="mb-3">
            <FaceCarousel :assets="allFaces" size="incident"/>
          </div>
          <div v-else>
            <img
              :src="firstFace.url"
              v-viewer
              alt="evidence"
              style="width: 100%"
              class="img-fluid"
            />
          </div>
        </div>
        <div v-else class="card-body">
          <div class="wrapper">
            <div class="sub-wrapper">
              <Loader />
            </div>
          </div>
        </div>
      </div>

      <div class="card m-t-sm">
        <div class="card-header">
          <h3 class="card-title">Timeline</h3>
        </div>
        <div class="card-body" style="max-height: 500px; overflow-y: scroll">
          <ul class="timeline">
            <li v-for="log of logs" :key="log.id + '-log'">
              <div class="timeline-time">
                {{ log.created_date }} : {{ log.created_time }} |
                {{ log.user ? getUserDisplayName(log.user.email) : "System" }}
              </div>
              <div class="timeline-content" style="white-space: pre-wrap">
                <p>{{ log.description }}</p>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
  
  <!-- MODALS -->

  <!-- Request Closure Modal -->
  <div
    class="modal fade"
    id="requestToCloseModal"
    tabindex="-1"
    aria-labelledby="requestToCloseModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="requestToCloseModalLabel">
            Request to Close
          </h5>
          <button
            type="button"
            class="btn-close btn-close-white"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label
              >Please select a reason for closing this incident</label
            >
            <select class="form-control" v-model="selectedClosureOption">
              <option
                v-for="option in closureOptions"
                :key="option"
                :value="option"
              >
                {{ option }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label for="reason"
              >Please explain why you would like to close this incident</label
            >
            <textarea
              class="form-control"
              rows="5"
              v-model="closure.reason"
            ></textarea>
            <span class="text-mute"
              >Minimum 10 characters not including spaces. (<span
                :class="{
                  'text-danger': closureReasonLength < 10,
                  'text-success': closureReasonLength > 10,
                }"
                >{{ closureReasonLength }}</span
              >/10)</span
            >
          </div>

          <div style="display: flex; justify-content: center; align-items: center;">
            <div v-if="!canCloseIncident">
              <label for="acknowledge">
                The individual reported has other open incident, are you sure you want to close this incident?
              </label>
              <input
                type="checkbox"
                id="acknowledge"
                v-model="canCloseAcknowledged"
              />
            </div>
            <div v-else>
              <p>No other incidents found.</p>
            </div>
          </div>

          <button
            :disabled="(closureReasonLength < 10 || !selectedClosureOption)|| (!canCloseIncident && !canCloseAcknowledged)" 
            class="btn btn-sm btn-primary float-right m-t-sm"
            @click="submitClosure"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Upload Modal -->
  <div
    class="modal fade"
    id="uploadModal"
    tabindex="-1"
    aria-labelledby="uploadModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="uploadModalLabel">Upload Evidence</h5>
          <button
            type="button"
            class="btn-close btn-close-white"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <p>
            Upload evidence to this incident. All files must have a
            description before you can upload them.
          </p>

          <p>Allowed file types:</p>
          <ul>
            <li>Images (PNG/JPG/GIF)</li>
            <li>Videos (MP4/MOV/AVI)</li>
            <li>PDF</li>
            <li>Word</li>
          </ul>

          <!-- <div class="form-group">
            <label for="file">Please arrange your evidence in the order of the incident. This will assist with the review process.</label>
            <div class="d-flex align-items-center mb-3">
              <button
                class="btn btn-outline-primary"
                @click="addMoreFiles($event, 'upload')"
              >
                Select Files to Upload
              </button>
              <span class="text-muted ms-3">(you can select multiple)</span>
            </div>
            <input
              id="uploadFileInput"
              accept="image/*,video/*,application/pdf,.doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              type="file"
              @change="selectFiles($event, 'upload')"
              class="form-control d-none"
              multiple
            />
          </div> -->

          <table class="table">
            <thead>
              <tr>
                <th>Name<span class="text-danger">*</span></th>
                <th>File</th>
                <th>Actions</th>
                <th>Order Items</th>
              </tr>
            </thead>
            <draggable tag="tbody" :list="uploadAssets.files" @end="onDragEnd" handle=".handle">
              <template #item="{element}">
                <tr :key="element.id">
                  <td style="min-width: 200px">
                    <span>{{ element.name || "Please Select File" }}</span>
                  </td>
                  <td>
                    <div class="form-control" style="display: flex; align-items: start; word-break: break-all;">
                      <input
                        @change="(ev) => selectFileForRow(ev, element, 'upload')"
                        accept="image/*,video/*,application/pdf,.doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                        type="file"
                        class="input-no-text"
                      />
                      <!-- <p v-if="element.rawFile">{{ element.rawFile.name }}</p> -->
                    </div>
                    <br>
                    <label>File Description:<span class="text-danger">*</span></label>
                    <input
                      v-model="element.description"
                      :disabled="!element.rawFile || uploadAssets.uploading"
                      type="text"
                      class="form-control"
                      placeholder="Description"
                    />
                    <span class="text-mute">
                      Please provide a description of the evidence you are providing. 
                      This will help our verification team understand what the evidence is showing.
                    </span>
                  </td>
                  <td>
                    <button
                      v-show="!uploadAssets.uploading"
                      class="btn btn-sm btn-outline-danger float-end"
                      @click="removeFile(element, 'upload')"
                      :disabled="uploadAssets.files.length === 1"
                    >
                      Remove
                    </button>
                    <p v-show="uploadAssets.uploading">
                      Progress:
                      {{ element.upload_progress ? element.upload_progress + "%" : "Pending" }}
                    </p>
                  </td>
                  <td class="handle">
                    <i class="material-icons">drag_handle</i>
                  </td>
                </tr>
              </template>
            </draggable>
            <tbody>
              <tr>
                <td colspan="4">
                  <button
                    class="btn btn-sm btn-outline-primary float-end"
                    @click="addEmptyFile('upload')"
                  >
                    Add Evidence
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <button
            class="btn btn-sm btn-primary float-right m-t-sm"
            :disabled="!readyToUpload && !uploadAssets.uploading"
            @click="upload('upload')"
          >
            Upload
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Reupload Modal -->
  <div
    class="modal fade"
    id="reuploadModal"
    tabindex="-1"
    aria-labelledby="reuploadModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="reuploadModalLabel">Reupload Evidence</h5>
          <button
            type="button"
            class="btn-close btn-close-white"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div><div class="modal-body">
          <div class="alert alert-info mb-3">
            <strong>Replacing:</strong> {{ reuploadFilename || "Unknown file" }}
            <div class="mt-1"><strong>Description:</strong> {{ reuploadDescription }}</div>
            <div class="mt-1"><strong>Required Format:</strong> .{{ reuploadExtension }}</div>
          </div>
        
          <p>Evidence filenames don't have to match. Reupload evidence for this incident.</p>

          <div class="form-group">
            <label for="reuploadFile">Select file to reupload</label>
            <input
              id="reuploadFile"
              accept="image/*,video/*,application/pdf,.doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              type="file"
              @change="selectFiles($event, 'reupload')"
              class="form-control"
              multiple
            />
          </div>
          <table class="table">
            <thead>
              <tr>
                <th>File</th>
                <th>Size</th>
                <th>{{ assetReupload.uploading ? "Progress" : "Actions" }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="file in assetReupload.files" :key="file.name">
                <td>{{ file.rawFile.name }}</td>
                <td>{{ file.humanSize }}</td>
                <td>
                  <button
                    v-show="!assetReupload.uploading"
                    class="btn btn-sm btn-danger"
                    @click="removeFile(file, 'reupload')"
                  >
                    Remove
                  </button>
                  <p v-show="assetReupload.uploading">
                    Progress:
                    {{
                      file.upload_progress
                        ? file.upload_progress + "%"
                        : "Pending"
                    }}
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="d-flex justify-content-between align-items-center">
            <div style="width: 100px"><!-- Empty space for balance --></div>
            
            <!-- Extension mismatch warning -->
            <div 
              v-if="assetReupload.files.length > 0 && !readyToReupload && !assetReupload.uploading"
              class="text-danger d-flex align-items-center"
            >
              <i class="material-icons me-1">error</i>
              File extension doesn't match required format (.{{ reuploadExtension }})
            </div>
            <div v-else class="flex-grow-1"><!-- Empty space when no warning --></div>

            <button
              class="btn btn-sm btn-primary m-t-sm"
              :disabled="!readyToReupload || assetReupload.uploading"
              @click="reupload"
            >
              Reupload
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Resubmit Modal -->
  <div
    class="modal fade"
    id="resubmitModal"
    tabindex="-1"
    aria-labelledby="resubmitModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="resubmitModalLabel">
            Resubmit Incident
          </h5>
          <button
            type="button"
            class="btn-close btn-close-white"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <div class="row">
            <p>
              This incident has been set to Awaiting Evidence, here you can
              resubmit it for another review.<br />Any new evidence you upload
              will be added to the existing evidence.
            </p>
            <p>
              Here is the reviewers reason for setting this to Awaiting
              Evidence: <br />
              <span
                ><strong>{{ incident.reason }}</strong></span
              >
            </p>

            <div class="col-12">
              <label for="resub_types" class="form-label"
                >Add Extra Types:
              </label>
              <Multiselect
                :class="{ 'multi-dark': $store.state.auth.status.dark }"
                mode="tags"
                placeholder="Select types"
                :close-on-select="false"
                :searchable="true"
                :create-option="true"
                :multiple="true"
                :options="availableTypes"
                label="name"
                track-by="type_id"
                name="resub_types"
                class="m-b-sm"
                v-model="resubmission.types"
              />
            </div>

            <div class="col-6">
              <label for="resub_police_force" class="form-label"
                >Select Police Force:
              </label>
              <Multiselect
                :class="{ 'multi-dark': $store.state.auth.status.dark }"
                mode="tags"
                placeholder="Select police force"
                :close-on-select="false"
                :searchable="true"
                :create-option="false"
                :multiple="false"
                :options="policeForces"
                name="resub_police_force"
                class="m-b-sm"
                v-model="resubmission.police_force"
              />
            </div>

            <div class="col-6">
              <label for="police_reference_resub" class="form-label"
                >Modify Police Reference:
              </label>
              <input
                type="text"
                class="form-control m-b-sm"
                id="police_reference_resub"
                v-model="resubmission.police_ref"
              />
            </div>

            <div class="col-12">
              <label for="incident_date_resub" class="form-label"
                >Modify Date:
              </label>
              <input
                type="datetime-local"
                class="form-control m-b-sm"
                id="incident_date_resub"
                v-model="resubmission.happened_at"
              />
            </div>

            <div class="col-12">
              <label for="incident_description_resub" class="form-label"
                >Modify Description:
              </label>
              <textarea
                class="form-control m-b-sm"
                rows="3"
                id="incident_description_resub"
                v-model="resubmission.description"
              ></textarea>
              <p class="text-mute">
                Please provide a detailed description of the incident. This
                will help us understand what happened.
                <span class="float-end">
                  Minimum 100 characters.
                  <span
                    :class="{
                      'text-warning': normalizedResubmissionDescription.length < 100,
                      'text-success': normalizedResubmissionDescription.length > 99,
                    }"
                  >
                    {{ normalizedResubmissionDescription.length || 0 }}
                  </span>/100
                </span>
              </p>
            </div>
          </div>

          <div class="row">
            <p><strong>Evidence: </strong></p>
            <div class="col-12 m-b-sm">
              <p>
                Upload evidence to this incident. All files must have a
                description before you can upload them.
              </p>

              <p>Allowed file types:</p>
              <ul>
                <li>Images (PNG/JPG/GIF)</li>
                <li>Videos (MP4/MOV/AVI)</li>
                <li>PDF</li>
                <li>Word</li>
              </ul>

              <!-- <div class="form-group">
                <label for="file">Please arrange your evidence in the order of the incident. This will assist with the review process.</label>
                <div class="d-flex align-items-center mb-3">
                  <button
                    class="btn btn-outline-primary"
                    @click="addMoreFiles($event, 'resubmission')"
                  >
                    Select Files to Upload
                  </button>
                  <span class="text-muted ms-3">(you can select multiple)</span>
                </div>
                <input
                  id="resubmissionFileInput"
                  accept="image/*,video/*,application/pdf,.doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                  type="file"
                  @change="selectFiles($event, 'resubmission')"
                  class="form-control d-none"
                  multiple
                />
              </div> -->

              <table class="table">
                <thead>
                  <tr>
                    <th>Name<span class="text-danger">*</span></th>
                    <th>File</th>
                    <th>Actions</th>
                    <th>Order Items</th>
                  </tr>
                </thead>
                <draggable tag="tbody" :list="resubmission.files" @end="onDragEnd" handle=".handle">
                  <template #item="{element}">
                    <tr :key="element.id">
                      <td style="min-width: 200px">
                        <span>{{ element.name || "Please Select File" }}</span>
                      </td>
                      <td>
                        <div class="form-control" style="display: flex; align-items: start; word-break: break-all;">
                          <input
                            @change="(ev) => selectFileForRow(ev, element, 'resubmission')"
                            accept="image/*,video/*,application/pdf,.doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                            type="file"
                            class="input-no-text"
                          />
                          <!-- <p v-if="element.rawFile">{{ element.rawFile.name }}</p> -->
                        </div>
                        <br>
                        <label>File Description:<span class="text-danger">*</span></label>
                        <input
                          v-model="element.description"
                          :disabled="!element.rawFile || resubmission.uploading"
                          type="text"
                          class="form-control"
                          placeholder="Description"
                        />
                        <span class="text-mute">
                          Please provide a description of the evidence you are providing. 
                          This will help our verification team understand what the evidence is showing.
                        </span>
                      </td>
                      <td>
                        <button
                          v-show="!resubmission.uploading"
                          class="btn btn-sm btn-outline-danger float-end"
                          @click="removeFile(element, 'resubmission')"
                          :disabled="resubmission.files.length === 1"
                        >
                          Remove
                        </button>
                        <p v-show="resubmission.uploading">
                          Progress:
                          {{ element.upload_progress ? element.upload_progress + "%" : "Pending" }}
                        </p>
                      </td>
                      <td class="handle">
                        <i class="material-icons">drag_handle</i>
                      </td>
                    </tr>
                  </template>
                </draggable>
                <tbody>
                  <tr>
                    <td colspan="4">
                      <button
                        class="btn btn-sm btn-outline-primary float-end"
                        @click="addEmptyFile('resubmission')"
                      >
                        Add Evidence
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <button
                :disabled="
                  this.resubmission.files.length === 0 ||
                  this.resubmission.files.some(
                    (file) => !file.description || file.description === ''
                  )
                "
                class="btn btn-sm btn-primary float-right"
                @click="submitResubmission"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    class="modal fade"
    id="uploadWarningModal"
    tabindex="-1"
    aria-labelledby="uploadWarningModalLabel"
    aria-hidden="true"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="uploadWarningModalLabel">
            Background Upload Information
          </h5>
        </div>
        <div class="modal-body">
          <div class="alert alert-warning">
            <i class="material-icons me-2">warning</i>
            <strong>Important:</strong> Your evidence files will be uploaded in the background.
          </div>
          
          <p>Please note the following:</p>
          <ul>
            <li>Your evidence files will continue to upload in the background</li>
            <li>Closing this tab or browser will <strong>cancel the upload</strong></li>
            <li>You will see upload progress on the incident page</li>
            <li>A notification will appear when all uploads are complete</li>
          </ul>
          
          <div class="form-check mt-3">
            <input class="form-check-input" type="checkbox" v-model="uploadWarningAcknowledged" id="acknowledgeCheck">
            <label class="form-check-label" for="acknowledgeCheck">
              I understand that I need to keep this tab open until the upload is complete
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button 
            type="button" 
            class="btn btn-primary" 
            :disabled="!uploadWarningAcknowledged"
            @click="confirmUploadWarning"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  </div>

</template>

<script>
import axios from "axios";
import authHeader from "../../services/auth-header";
import Loader from "../dashboard/Customised/Loader.vue";
import { Multiselect } from "vue-multiselect";
import { v4 } from "uuid";
import { DateTime } from "luxon";
import backgroundUploadService from '../../services/backgroundUploadService';
import draggable from 'vuedraggable';
import FaceCarousel from "../../components/FaceCarousel.vue";

export default {
  components: {
    Loader,
    Multiselect,
    draggable,
    FaceCarousel
  },
  data() {
    return {
      incident: {
        id: this.$route.params.id,
      },
      assets: [],
      logs: [],
      types: [],
      witnesses: [],
      incidentNotes: [],
      newNote: '',
      hoveredNote: null,
      editedNoteId: null,
      editedNote: '',
      witness: {
        name: "",
        type: "",
        notes: "",
        submitting: false,
      },
      closure: {
        modal: null,
        reason: "",
        submitting: false,
        errors: [],
      },
      closureOptions: [
        "Resolved with the person",
        "Incident details incorrect",
        "Duplicate incident",
        "No longer relevant",
        "Other",
      ],
      selectedClosureOption: null,
      editing: false,
      availableTypes: [],
      original: {
        incident: {},
        assets: [],
        logs: [],
        types: [],
      },
      saving: false,
      uploadAssets: {
        modal: null,
        files: [],
        uploading: false,
      },
      uploadInProgress: false,
      uploadProgress: {
        total: 0,
        completed: 0,
        status: 'idle',
        files: [],
        overallProgress: 0
      },
      uploadWarningModal: null,
      uploadWarningAcknowledged: false,
      currentUploadType: null,
      policeForces: [
        "Avon and Somerset Constabulary",
        "Bedfordshire Police",
        "Cambridgeshire Constabulary",
        "Cheshire Constabulary",
        "City of London Police",
        "Cleveland Police",
        "Cumbria Constabulary",
        "Derbyshire Constabulary",
        "Devon & Cornwall Police",
        "Dorset Police",
        "Durham Constabulary",
        "Essex Police",
        "Gloucestershire Constabulary",
        "Greater Manchester Police",
        "Hampshire Constabulary",
        "Hertfordshire Constabulary",
        "Humberside Police",
        "Kent Police",
        "Lancashire Constabulary",
        "Leicestershire Police",
        "Lincolnshire Police",
        "Merseyside Police",
        "Metropolitan Police Service",
        "Norfolk Constabulary",
        "North Yorkshire Police",
        "Northamptonshire Police",
        "Northumbria Police",
        "Nottinghamshire Police",
        "South Yorkshire Police",
        "Staffordshire Police",
        "Suffolk Constabulary",
        "Surrey Police",
        "Sussex Police",
        "Thames Valley Police",
        "Warwickshire Police",
        "West Mercia Police",
        "West Midlands Police",
        "West Yorkshire Police",
        "Wiltshire Police",
      ],
      resubmission: {
        resubmitting: false,
        modal: null,
        types: [],
        police_force: null,
        police_ref: null,
        happened_at: null,
        description: null,
        files: [],
        uploading: false,
      },
      hoveredFileId: null,
      reuploadLink: null,
      assetReupload: {
        modal: null,
        files: [],
        uploading: false,
      },
      reuploadFilename: null,
      reuploadDescription: null,
      reuploadExtension: null,
      canCloseIncident: false,
      canCloseAcknowledged: false,
      tooltipVisible: false,
      assetCounters: {
        photo: 0, 
        video: 0, 
        pdf: 0,
        doc: 0, 
        text: 0,
        file: 0,
        unknown: 0
      },
      drag: false,
    };
  },
  mounted() {
    this.closure.modal = new bootstrap.Modal(
      document.getElementById("requestToCloseModal")
    );
    this.uploadAssets.modal = new bootstrap.Modal(
      document.getElementById("uploadModal")
    );
    this.resubmission.modal = new bootstrap.Modal(
      document.getElementById("resubmitModal")
    );
    this.assetReupload.modal = new bootstrap.Modal(
      document.getElementById("reuploadModal")
    );
    this.uploadWarningModal = new bootstrap.Modal(
      document.getElementById("uploadWarningModal"),
      { keyboard: false, backdrop: 'static' }
    );

    this.uploadAssets.files = [{
      id: v4(),
      rawFile: null,
      name: "",
      description: "",
      upload_progress: 0,
      fileType: null
    }];
    
    this.resubmission.files = [{
      id: v4(),
      rawFile: null,
      name: "",
      description: "",
      upload_progress: 0,
      fileType: null
    }];
  
    this.getIncident();
    this.getTypes();

    window.addEventListener('bg-upload-status', this.handleBackgroundUploadStatus);

    if (sessionStorage.getItem("activeUploads") === "true" &&
      sessionStorage.getItem("activeIncidentId") === this.incident.id) {

      const status = backgroundUploadService.getStatus();
      
      if (status.active) {
        this.uploadInProgress = true;
        this.uploadProgress.status = 'uploading';
        this.uploadProgress.files = status.uploads;
        this.uploadProgress.total = status.uploads.length;
        this.uploadProgress.completed = status.uploads.filter(u => u.status === 'complete').length;
        this.uploadProgress.overallProgress = status.progress;
      }
    }
  },
  beforeUnmount() {
    window.removeEventListener('bg-upload-status', this.handleBackgroundUploadStatus);
  },
  methods: {
    getIncident() {
      axios
        .get(`https://api.varsanpr.com/customer/incident/incidents`, {
          params: {
            incident_id: this.incident.id,
            site_id: this.$route.query.site_id || 0,
            context:"detail",
          },
          headers: authHeader(),
        })
        .then((response) => {
          response.data.incident.happened_at = new Date(
            response.data.incident.happened_at
          )
            .toISOString()
            .slice(0, 16);
          this.incident = response.data.incident;
          this.assets = response.data.assets;
          this.assets.forEach((asset) => {
            asset.filetype = this.determineAssetType(
              asset.filename,
              asset.mime || ""
            );
          });
          response.data.logs.forEach((log) => {
            log.created_date = new Date(log.created_at).toLocaleDateString();
            log.created_time = new Date(log.created_at).toLocaleTimeString();
          });
          this.logs = response.data.logs;
          this.types = response.data.types;
          response.data.witnesses.forEach((witness) => {
            witness.created_date = new Date(
              witness.created_at
            ).toLocaleDateString();
            witness.created_time = new Date(
              witness.created_at
            ).toLocaleTimeString();
          });
          this.witnesses = response.data.witnesses;
          this.getNotes();
          this.checkForOtherIncidents();
        })
        .catch((error) => {
          this.$error(
            error.response.data.message || "Error loading incident data",
            "Error"
          );
        });
    },
    determineAssetType(filename, mime) {
      if (mime.includes("image")) return "image";
      if (mime.includes("video")) return "video";
      if (mime.includes("pdf")) return "pdf";
      if (mime.includes("word")) return "word";
      if (mime.includes("excel")) return "excel";
      if (mime.includes("powerpoint")) return "powerpoint";
      if (mime.includes("text")) return "text";

      switch (filename.slice(filename.lastIndexOf("."))) {
        case ".jpg":
        case ".jpeg":
        case ".png":
        case ".gif":
        case ".bmp":
        case ".webp":
        case ".svg":
        case ".ico":
        case ".tif":
        case ".tiff":
          return "image";
        case ".mp4":
        case ".webm":
        case ".ogg":
        case ".avi":
        case ".mov":
        case ".flv":
        case ".wmv":
        case ".mkv":
          return "video";
        case ".pdf":
          return "pdf";
        case ".doc":
        case ".docx":
          return "word";
        case ".xls":
        case ".xlsx":
          return "excel";
        case ".ppt":
        case ".pptx":
          return "powerpoint";
        case ".txt":
          return "text";
        default:
          return "unknown";
      }
    },
    toggleEdit() {
      if (this.editing) {
        // Check the user actually wants to cancel and lose changes
        if (
          !confirm(
            "Are you sure you want to cancel editing? All changes will be lost."
          )
        ) {
          return;
        }
      }
      this.editing = !this.editing;
      this.$refs.editButton.blur();

      if (this.editing) {
        this.original.incident = JSON.parse(JSON.stringify(this.incident));
        this.original.assets = JSON.parse(JSON.stringify(this.assets));
        this.original.logs = JSON.parse(JSON.stringify(this.logs));
        this.original.types = JSON.parse(JSON.stringify(this.types));
      } else {
        this.incident = JSON.parse(JSON.stringify(this.original.incident));
        this.assets = JSON.parse(JSON.stringify(this.original.assets));
        this.logs = JSON.parse(JSON.stringify(this.original.logs));
        this.types = JSON.parse(JSON.stringify(this.original.types));

        this.original = {
          incident: {},
          assets: [],
          logs: [],
          types: [],
        };
      }
    },
    getTypes() {
      axios
        .get(`https://api.varsanpr.com/customer/incident/incidents/types`, {
          headers: authHeader(),
        })
        .then((response) => {
          response.data.types.forEach((type) => {
            type.type_id = type.id;
          });
          this.availableTypes = response.data.types;
        })
        .catch((error) => {
          this.$error(
            error.response.data.message || "Error loading incident types",
            "Error"
          );
        });
    },
    saveChanges() {
      if (this.saving) return;
      this.saving = true;

      let data = {
        incident_id: this.incident.id,
        site_id: this.incident.site_id,
      };

      // Only send what we have updated
      if (this.incident.happened_at !== this.original.incident.happened_at) {
        data.happened_at = DateTime.fromISO(this.incident.happened_at).toISO({
          includeOffset: true,
        });
      }

      if (this.incident.police_force !== this.original.incident.police_force) {
        data.police_force = this.incident.police_force;
      }

      if (this.incident.police_ref !== this.original.incident.police_ref) {
        data.police_ref = this.incident.police_ref;
      }

      if (this.incident.description !== this.original.incident.description) {
        data.description = this.incident.description;
      }

      if (this.types !== this.original.types) {
        data.types = this.types.map((type) => type.type_id);
      }

      axios
        .patch(`https://api.varsanpr.com/customer/incident/incidents`, data, {
          headers: authHeader(),
        })
        .then((response) => {
          this.$success("Incident updated successfully", "Success");
          this.getIncident();
          this.toggleEdit();
        })
        .catch((error) => {
          this.$error(
            error.response.data.message || "Error updating incident",
            "Error"
          );
        });
    },
    selectFiles(ev, type) {
      const filesArray =
        type === "resubmission" ? this.resubmission :
        type === "reupload" ? this.assetReupload : this.uploadAssets;

        if (type === "reupload") {
          // Only take the first file
          if (ev.target.files.length > 0) {
            const file = ev.target.files[0];
            const fileType = this.getFileType(file);
            
            // Clear existing files and add the new one
            filesArray.files = [{
              rawFile: file,
              humanSize: this.humanFileSize(file.size),
              name: file.name,
              description: "",
              upload_progress: 0,
              fileType: fileType
            }];
          }
          return;
        }

      for (let file of ev.target.files) {
        const fileType = this.getFileType(file);
        const fileName = this.generateAssetName(fileType);
        
        filesArray.files.push({
          rawFile: file,
          humanSize: this.humanFileSize(file.size),
          name: fileName,
          description: "",
          upload_progress: 0,
          fileType: fileType
        });
      }

      if (type === "resubmission") {
        console.log(this.resubmission.files);
      } else if (type === "reupload") {
        console.log(this.assetReupload.files);
      } else {
        console.log(this.uploadAssets.files);
      }
    },
    humanFileSize(size) {
      let i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
      return (
        (size / Math.pow(1024, i)).toFixed(2) * 1 +
        " " +
        ["B", "KB", "MB", "GB", "TB"][i]
      );
    },
    removeFile(file, type) {
      if (type === "reupload") {
        this.assetReupload.files = [];
        return;
      }

      const filesArray = type === "resubmission" ? this.resubmission : this.uploadAssets;

      if (filesArray.files.length <= 1) return;
      const fileType = file.rawFile ? this.getFileType(file.rawFile) : null;
      filesArray.files = filesArray.files.filter((f) => f !== file);
      
      if (fileType) {
        const remainingFiles = filesArray.files.filter(f => 
          f.rawFile && this.getFileType(f.rawFile) === fileType
        );
        
        remainingFiles.sort((a, b) => {
          const aMatch = a.name ? a.name.match(/^(\w+)\s+(\d+)$/) : null;
          const bMatch = b.name ? b.name.match(/^(\w+)\s+(\d+)$/) : null;
          const aNum = aMatch ? parseInt(aMatch[2], 10) : 0;
          const bNum = bMatch ? parseInt(bMatch[2], 10) : 0;
          return aNum - bNum;
        });
        
        let startingNumber = 1;
        if (this.assets && this.assets.length > 0) {
          this.assets.forEach(asset => {
            const match = asset.name ? asset.name.match(/^(\w+)\s+(\d+)$/) : null;
            if (match) {
              const [_, typePart, numberPart] = match;
              const assetType = typePart.toLowerCase();
              if (assetType === fileType) {
                startingNumber = Math.max(startingNumber, parseInt(numberPart, 10) + 1);
              }
            }
          });
        }
        
        for (let i = 0; i < remainingFiles.length; i++) {
          remainingFiles[i].name = `${fileType.charAt(0).toUpperCase() + fileType.slice(1)} ${startingNumber + i}`;
        }
        
        if (remainingFiles.length > 0) {
          this.assetCounters[fileType] = startingNumber + remainingFiles.length - 1;
        } else {
          let highestNumber = 0;
          if (this.assets && this.assets.length > 0) {
            this.assets.forEach(asset => {
              const match = asset.name ? asset.name.match(/^(\w+)\s+(\d+)$/) : null;
              if (match) {
                const [_, typePart, numberPart] = match;
                const assetType = typePart.toLowerCase();
                if (assetType === fileType) {
                  highestNumber = Math.max(highestNumber, parseInt(numberPart, 10));
                }
              }
            });
          }
          this.assetCounters[fileType] = highestNumber;
        }
      }
    },
    async upload(type) {
      const filesArray = type === "resubmission" ? this.resubmission : this.uploadAssets;

      if (filesArray.files.length === 0 || filesArray.uploading) return;

      this.uploadWarningAcknowledged = false;
      this.currentUploadType = type;

      this.uploadAssets.modal.hide();
      this.uploadWarningModal.show();
    },
    confirmUploadWarning() {
      if (!this.uploadWarningAcknowledged) return;

      this.uploadWarningModal.hide();

      if (this.currentUploadType === 'reupload') {
        this.startReuploadProcess();
      } else if (this.currentUploadType === 'resubmission') {
        this.startUploadProcess('resubmission');
      } else {
        this.startUploadProcess('upload');
      }
    },
    async startUploadProcess(type) {
      const filesArray = type === "resubmission" ? this.resubmission : this.uploadAssets;
      filesArray.uploading = true;

      this.uploadInProgress = true;
      this.uploadProgress.status = 'uploading';
      this.uploadProgress.completed = 0;
      this.uploadProgress.total = filesArray.files.length;
      this.uploadProgress.overallProgress = 0;
      this.uploadProgress.files = [];

      backgroundUploadService.setIncidentId(this.incident.id);

      sessionStorage.setItem("activeUploads", "true");
      sessionStorage.setItem("activeIncidentId", this.incident.id);
          
      for (const file of filesArray.files) {
        try {
          const uploadId = await backgroundUploadService.uploadAsset(
            this.incident.id,
            this.incident.site_id,
            file.rawFile,
            file.name || file.rawFile.name,
            file.description
          );
          
          backgroundUploadService.trackUpload(uploadId, file.rawFile, {
            onProgress: (progress) => {
              file.upload_progress = progress;
            },
            onComplete: () => {
              console.log(`Upload complete for ${file.rawFile.name}`);
            },
            onError: (error) => {
              console.error(`Upload failed for ${file.rawFile.name}:`, error);
              this.$error(`Failed to upload ${file.rawFile.name}`, error);
            }
          });
        } catch (error) {
          console.error(`Failed to start upload for ${file.rawFile.name}:`, error);
          this.$error(`Failed to start upload for ${file.rawFile.name}`, error);
        }
      }

      this.$success(`${filesArray.files.length} files are uploading in the background.`);
      
      filesArray.modal.hide();
      filesArray.uploading = false;
    },
    getReuploadLink(asset_id) {
      const asset = this.evidence.find(file => file.id === asset_id);

      axios.get(`https://api.varsanpr.com/customer/incident/incidents/getReuploadLink`, {
        params: {
          incident_id: this.incident.id,
          site_id: this.$route.query.site_id || 0,
          asset_id: asset_id,
        },
        headers: authHeader(),
      })
      .then((response) => {
        this.reuploadLink = response.data.sas;
        this.reuploadAssetId = asset_id;
        this.reuploadFilename = asset.filename.split('/').pop(); 
        this.reuploadDescription = asset.description || "No description"; 
        this.reuploadExtension = asset.filename.split('.').pop().toLowerCase();
        this.assetReupload.files = [];
        this.assetReupload.modal.show();
      })
      .catch((error) => {
        this.$error(
          error.response.data.message || "Error getting reupload link",
          "Error"
        );
      });
    },
    reupload() {
      if (this.assetReupload.files.length === 0 || this.assetReupload.uploading) return;
  
      this.uploadWarningAcknowledged = false;
      this.currentUploadType = 'reupload';
      this.assetReupload.modal.hide();
      this.uploadWarningModal.show();
    },
    async startReuploadProcess() {
      const filesArray = this.assetReupload;
      const file = filesArray.files[0];

      filesArray.uploading = true;
      this.uploadInProgress = true;
      this.uploadProgress.status = 'uploading';
      
      backgroundUploadService.setIncidentId(this.incident.id);
  
      try {
        const uploadId = await backgroundUploadService.reuploadAsset(
          this.incident.id,
          this.incident.site_id,
          file.rawFile,
          this.reuploadAssetId,
          this.reuploadLink
        );
        
        backgroundUploadService.trackUpload(uploadId, file.rawFile, {
          onProgress: (progress) => {
            file.upload_progress = progress;
          },
          onComplete: () => {
            console.log(`Reupload complete for ${file.rawFile.name}`);
            
            setTimeout(() => {
              this.getIncident();
            }, 1000);
          },
          onError: (error) => {
            console.error(`Reupload failed for ${file.rawFile.name}:`, error);
            this.$error(`Failed to reupload ${file.rawFile.name}`, error);
          }
        });

        this.$success(`${file.rawFile.name} is being reuploaded in the background.`);
      } catch (error) {
        console.error(`Failed to start reupload:`, error);
        this.$error("Failed to start reupload", error);
      } finally {
        filesArray.modal.hide();
        filesArray.uploading = false;
      }

    },
    addWitness() {
      if (this.witness.submitting) return;
      this.witness.submitting = true;

      let data = {
        site_id: this.incident.site_id,
        name: this.witness.name,
        type: this.witness.type,
        notes: this.witness.notes,
      };

      axios
        .post(
          `https://api.varsanpr.com/customer/incident/incidents/${this.incident.id}/witness`,
          data,
          {
            headers: authHeader(),
          }
        )
        .then((response) => {
          this.$success("Witness added successfully", "Success");
          this.witness = {
            name: "",
            type: "",
            notes: "",
            submitting: false,
          };
          this.getIncident();
        })
        .catch((error) => {
          this.$error(
            error.response.data.message || "Error adding witness",
            "Error"
          );
          this.witness.submitting = false;
        });
    },
    removeWitness(witness) {
      if (witness.submitting) return;
      witness.submitting = true;

      axios
        .delete(
          `https://api.varsanpr.com/customer/incident/incidents/${this.incident.id}/witness/${witness.id}`,
          {
            params: {
              site_id: this.incident.site_id,
            },
            headers: authHeader(),
          }
        )
        .then((response) => {
          this.$success("Witness removed successfully", "Success");
          this.getIncident();
        })
        .catch((error) => {
          this.$error(
            error.response.data.message || "Error removing witness",
            "Error"
          );
          witness.submitting = false;
        });
    },
    getNotes() {
      axios
        .get(`https://api.varsanpr.com/customer/incident/incidents/notes`, {
          params: {
            incident_id: this.incident.id,
          },
          headers: authHeader(),
        })
        .then((response) => {
          response.data.notes.forEach((note) => {
            note.created_date = new Date(note.created_at).toLocaleDateString();
            note.created_time = new Date(note.created_at).toLocaleTimeString();
          });
          this.incidentNotes = response.data.notes;
        })
        .catch((error) => {
          this.$error(
            error.response.data.message || "Error loading incident notes",
            "Error"
          );
        });
    },
    addNote() {
      if (!this.newNote.trim()) return;

      const data = {
        note: this.newNote,
      };

      console.log(data);

      axios
        .post(`https://api.varsanpr.com/customer/incident/incidents/notes?incident_id=${this.incident.id}`, data, {
          headers: authHeader(),
        })
        .then((response) => {
          this.$success("Note added successfully", "Success");
          this.newNote = "";
          this.getNotes();
        })
        .catch((error) => {
          this.$error(
            error.response.data.message || "Error adding note",
            "Error"
          );
        });
    },
    toggleNoteEdit(note) {
      if (this.editedNoteId === null) {
        this.editedNoteId = note.id;
        this.editedNote = note.note;
      } else if (this.editedNoteId === note.id) {
        this.editedNoteId = null;
        this.editedNote = '';
      }
    },
    saveEdit(note) {
      if (!this.editedNote.trim()) return;

      const data = {
        note: this.editedNote,
      };

      axios
        .patch(`https://api.varsanpr.com/customer/incident/incidents/notes?incident_id=${this.incident.id}&note_id=${note.id}`, data, {
          headers: authHeader(),
        })
        .then((response) => {
          this.$success("Note updated successfully", "Success");
          this.editedNoteId = null;
          this.editedNote = '';
          this.getNotes();
        })
        .catch((error) => {
          this.$error(
            error.response.data.message || "Error updating note",
            "Error"
          );
        });
    },
    submitClosure() {
      if (this.closure.submitting) return;
      this.closure.submitting = true;

      let reason = `${this.selectedClosureOption}: ${this.closure.reason}`;

      let data = {
        site_id: this.incident.site_id,
        reason: reason,
      };

      axios
        .post(
          `https://api.varsanpr.com/customer/incident/incidents/${this.incident.id}/close`,
          data,
          {
            headers: authHeader(),
          }
        )
        .then((response) => {
          this.$success("Closure request submitted successfully", "Success");
          this.closure.modal.hide();
          this.getIncident();
        })
        .catch((error) => {
          this.$error(
            error.response.data.message || "Error submitting closure request",
            "Error"
          );
          this.closure.submitting = false;
        });
    },
    addAsset() {
      this.resubmission.files.push({
        id: v4(),
        file: null,
        name: null,
        description: null,
      });
    },
    removeAsset(asset) {
      this.resubmission.files = this.resubmission.files.filter(
        (a) => a.id !== asset.id
      );
    },
    checkForOtherIncidents() {
      axios
        .get(`https://api.varsanpr.com/customer/incident/incidents/${this.incident.id}/close/check`, {
          params: {
            site_id: this.incident.site_id,
          },
          headers: authHeader(),
        })
        .then((response) => {
          this.canCloseIncident = response.data.canClose;

        })
        .catch((error) => {
          this.$error(
            error.response.data.message || "Error checking for other incidents",
            "Error"
          );
        });
    },
    openResubmissionModal() {
      this.initializeAssetCounters();

      if (this.resubmission.types.length <= 0) {
        this.resubmission.types = this.types;
      }
      if (
        this.resubmission.happened_at === null &&
        this.incident?.happened_at
      ) {
        this.resubmission.happened_at = this.incident.happened_at;
      }
      if (this.resubmission.police_force === null) {
        this.resubmission.police_force = this.incident.police_force;
      }
      if (this.resubmission.police_ref === null) {
        this.resubmission.police_ref = this.incident.police_ref;
      }
      if (this.resubmission.description === null) {
        this.resubmission.description = this.normalizeDescription(this.incident.description);
      }

      if (this.resubmission.files.length === 0) {
        this.resubmission.files = [{
          id: v4(),
          rawFile: null,
          name: "",
          description: "",
          upload_progress: 0,
          fileType: null
        }];
      }

      this.resubmission.modal.show();
    },
    submitResubmission() {
      if (this.resubmission.resubmitting) return;
      this.resubmission.resubmitting = true;

      let data = {
        incident_id: this.incident.id,
        site_id: this.incident.site_id,
        resubmit: true,
      };

      if (this.resubmission.types !== this.incident.types) {
        data.types = this.resubmission.types.map((type) => type.type_id);
      }
      if (this.resubmission.happened_at !== this.incident.happened_at) {
        data.happened_at = DateTime.fromISO(
          this.resubmission.happened_at
        ).toISO({
          includeOffset: true,
        });
      }
      if (this.resubmission.police_force !== this.incident.police_force) {
        data.police_force = this.resubmission.police_force;
      }
      if (this.resubmission.police_ref !== this.incident.police_ref) {
        data.police_ref = this.resubmission.police_ref;
      }
      if (this.resubmission.description !== this.incident.description) {
        data.description = this.resubmission.description;
      }

      axios
        .patch(`https://api.varsanpr.com/customer/incident/incidents`, data, {
          headers: authHeader(),
        })
        .then((response) => {
          this.$success("Incident resubmitted successfully", "Success");
          this.upload("resubmission");
        })
        .catch((error) => {
          this.$error(
            error.response.data.message || "Error updating incident",
            "Error"
          );
        });
    },
    getUserDisplayName(email) {
      if (!email) return "Unknown";
      return email.endsWith("@varstechnology.co.uk") ? "VARS Staff" : email;
    },
    awaitingEvidenceCheck() {
      if (this.incident?.status === "AwaitingEvidence") {
        const userConfirmed = confirm("This incident is Awaiting Evidence and uploading new evidence here will not resubmit the incident! Please use the resubmission form for another review.");
        if (userConfirmed) {
          this.openResubmissionModal();
        }
      } else {
        this.initializeAssetCounters();
        this.uploadAssets.modal.show();
      }
    },
    initializeAssetCounters() {
      Object.keys(this.assetCounters).forEach(key => {
        this.assetCounters[key] = 0;
      });
      
      if (this.assets && this.assets.length > 0) {
        this.assets.forEach(asset => {
          const match = asset.name ? asset.name.match(/^(\w+)\s+(\d+)$/) : null;
          if (match) {
            const [_, typePart, numberPart] = match;
            const fileType = typePart.toLowerCase();
            const number = parseInt(numberPart, 10);
            
            if (this.assetCounters.hasOwnProperty(fileType) && number > this.assetCounters[fileType]) {
              this.assetCounters[fileType] = number;
            }
          }
        });
      }
    },
    normalizeDescription(description) {
      return description ? description.replace(/\s+/g, ' ').trim() : '';
    },
    showTooltip() {
      this.tooltipVisible = true;
    },
    hideTooltip() {
      this.tooltipVisible = false;
    },
    handleBackgroundUploadStatus(event) {
      if (event.detail.incidentId === this.incident.id) {
        // Direct assignment instead of $set
        this.uploadInProgress = event.detail.active;
        
        if (event.detail.files && event.detail.files.length > 0) {
          const newFiles = JSON.parse(JSON.stringify(event.detail.files));
          this.uploadProgress.files = newFiles;
          this.uploadProgress.total = event.detail.files.length;
          this.uploadProgress.completed = event.detail.files.filter(f => f.status === 'complete').length;
          
          let totalProgress = 0;
          event.detail.files.forEach(file => {
            totalProgress += file.status === 'complete' ? 100 : (file.progress || 0);
          });
          
          const overallProgress = Math.round(totalProgress / event.detail.files.length);
          this.uploadProgress.overallProgress = overallProgress;
          this.uploadProgress.status = event.detail.active ? 'uploading' : 'complete';
          
          // Force Vue to update the component
          this.$nextTick(() => this.$forceUpdate());
        }
        
        if (event.detail.completed) {
          this.getIncident();
          setTimeout(() => {
            this.uploadProgress.status = 'idle';
            this.uploadInProgress = false;
          }, 3000);
        }
      }
    },
    getFileType(file) {
      if (file.type.startsWith('image/')) {
        return 'photo';
      } else if (file.type.startsWith('video/')) {
        return 'video';
      } else if (file.type === 'application/pdf') {
        return 'pdf';
      } else if (file.type.includes('word') || 
                file.type === 'application/msword' || 
                file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        return 'doc';
      } else if (file.type === 'text/plain') {
        return 'text';
      } else {
        return 'unknown';
      }
    },
    generateAssetName(fileType) {
      this.assetCounters[fileType]++;
      return `${fileType.charAt(0).toUpperCase() + fileType.slice(1)} ${this.assetCounters[fileType]}`;
    },
    renumberAssets(fileType) {
      const filesArray = this.uploadAssets.files.filter(f => f.rawFile && this.getFileType(f.rawFile) === fileType);
      
      const fileNumbers = filesArray
        .map(file => {
          const match = file.name ? file.name.match(/^(\w+)\s+(\d+)$/) : null;
          return match ? parseInt(match[2], 10) : 0;
        })
        .filter(num => !isNaN(num));
      
      if (fileNumbers.length > 0) {
        this.assetCounters[fileType] = Math.max(...fileNumbers);
      }
    },
    recountAssetNumbers() {
      Object.keys(this.assetCounters).forEach(key => {
        this.assetCounters[key] = 0;
      });
      
      if (this.assets && this.assets.length > 0) {
        this.assets.forEach(asset => {
          const match = asset.name ? asset.name.match(/^(\w+)\s+(\d+)$/) : null;
          if (match) {
            const [_, typePart, numberPart] = match;
            const fileType = typePart.toLowerCase();
            const number = parseInt(numberPart, 10);
            
            if (this.assetCounters.hasOwnProperty(fileType) && number > this.assetCounters[fileType]) {
              this.assetCounters[fileType] = number;
            }
          }
        });
      }
    },
    renumberFilesOfType(fileType, filesArray) {
      const filesOfType = filesArray.filter(f => 
        f.rawFile && this.getFileType(f.rawFile) === fileType
      );
      
      if (filesOfType.length <= 1) return;
      if (!this.drag) return;
      
      let startingNumber = 1;
      if (this.assets && this.assets.length > 0) {
        this.assets.forEach(asset => {
          const match = asset.name ? asset.name.match(/^(\w+)\s+(\d+)$/) : null;
          if (match) {
            const [_, typePart, numberPart] = match;
            const assetType = typePart.toLowerCase();
            if (assetType === fileType) {
              const number = parseInt(numberPart, 10);
              startingNumber = Math.max(startingNumber, number + 1);
            }
          }
        });
      }
      
      for (let i = 0; i < filesOfType.length; i++) {
        const file = filesOfType[i];
        const number = startingNumber + i;
        file.name = `${fileType.charAt(0).toUpperCase() + fileType.slice(1)} ${number}`;
      }
      
      this.assetCounters[fileType] = startingNumber + filesOfType.length - 1;
    },
    onDragEnd() {
      this.drag = true;
      
      const fileTypes = ['photo', 'video', 'pdf', 'doc', 'text', 'file', 'unknown'];
      
      fileTypes.forEach(fileType => {
        this.renumberFilesOfType(fileType, this.uploadAssets.files);
        this.renumberFilesOfType(fileType, this.resubmission.files);
      });
      
      this.drag = false;
    },
    // addMoreFiles(event, type) {
    //   event.preventDefault();
      
    //   const fileInputId = type === 'resubmission' ? 'resubmissionFileInput' : 'uploadFileInput';
    //   const fileInput = document.getElementById(fileInputId);
      
    //   if (fileInput) {
    //     fileInput.click();
    //   }
    // },
    addEmptyFile(type) {
      const filesArray = type === "resubmission" ? this.resubmission : this.uploadAssets;
      
      filesArray.files.push({
        id: v4(),
        rawFile: null,
        name: "",
        description: "",
        upload_progress: 0,
        fileType: null
      });
    },
    selectFileForRow(ev, file, type) {
      if (!ev.target.files || !ev.target.files[0]) {
        if (file.rawFile) {
          file.rawFile = null;
          file.name = "";
          file.fileType = null;
          file.humanSize = null;
        }
        return;
      }
      
      const selectedFile = ev.target.files[0];
      const newFileType = this.getFileType(selectedFile);
      
      const isReplacement = file.rawFile !== null;
      const oldFileType = isReplacement ? this.getFileType(file.rawFile) : null;
      const oldNumberMatch = isReplacement && file.name ? file.name.match(/^(\w+)\s+(\d+)$/) : null;
      const oldNumber = oldNumberMatch ? parseInt(oldNumberMatch[2], 10) : null;
      
      if (isReplacement && oldFileType !== newFileType) {
        this.recountAssetNumbers();
      } else if (isReplacement && oldFileType === newFileType && oldNumber !== null) {
        file.rawFile = selectedFile;
        file.humanSize = this.humanFileSize(selectedFile.size);
        file.fileType = newFileType;
        file.name = `${newFileType.charAt(0).toUpperCase() + newFileType.slice(1)} ${oldNumber}`;
        return;
      }
      
      const filesArray = type === "resubmission" ? this.resubmission.files : this.uploadAssets.files;
      const fileName = this.findNextAvailableName(newFileType, filesArray);
      
      file.rawFile = selectedFile;
      file.humanSize = this.humanFileSize(selectedFile.size);
      file.name = fileName;
      file.fileType = newFileType;
    },
    findNextAvailableName(fileType, filesArray) {
      let highestNumber = 0;
      
      if (this.assets && this.assets.length > 0) {
        this.assets.forEach(asset => {
          const match = asset.name ? asset.name.match(/^(\w+)\s+(\d+)$/) : null;
          if (match) {
            const [_, typePart, numberPart] = match;
            const assetType = typePart.toLowerCase();
            if (assetType === fileType) {
              const number = parseInt(numberPart, 10);
              highestNumber = Math.max(highestNumber, number);
            }
          }
        });
      }
      
      filesArray.forEach(file => {
        if (file.rawFile && this.getFileType(file.rawFile) === fileType && file.name) {
          const match = file.name.match(/^(\w+)\s+(\d+)$/);
          if (match) {
            const number = parseInt(match[2], 10);
            highestNumber = Math.max(highestNumber, number);
          }
        }
      });
      
      const nextNumber = highestNumber + 1;
      this.assetCounters[fileType] = nextNumber;
      return `${fileType.charAt(0).toUpperCase() + fileType.slice(1)} ${nextNumber}`;
    },
    scrollToMissingEvidence() {
      if (this.missingEvidence.length > 0) {
        const id = this.missingEvidence[0].id;
        const el = document.getElementById(`missing-evidence-${id}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Highlight the element briefly
          el.classList.add('highlight-element');
          setTimeout(() => {
            el.classList.remove('highlight-element');
          }, 2000);
        }
      }
    },
  },
  computed: {
    severity() {
      const pendingStatuses = ["PendingAsset", "Pending", "PendingClosed", "AwaitingEvidence"];
      const status = this.incident?.status || "";

      if (pendingStatuses.includes(status)) {
        return "Pending Review";
      } 

      if (status === "Closed" || status === "Rejected") {
        return "Closed";
      }

      let severityValue = this.incident?.severity || -1;
      if (severityValue === -1) return "unknown";
      if (severityValue < 250) {
        return "low";
      } else if (severityValue < 500) {
        return "med";
      } else if (severityValue < 750) {
        return "high";
      } else if (severityValue >= 750) {
        return "critical";
      } else {
        return "unknown";
      }
    },
    incident_date() {
      return new Date(this.incident?.happened_at).toLocaleDateString();
    },
    incident_time() {
      return new Date(this.incident?.happened_at).toLocaleTimeString();
    },
    created_date() {
      return new Date(this.incident?.created_at).toLocaleDateString();
    },
    created_time() {
      return new Date(this.incident?.created_at).toLocaleTimeString();
    },
    firstFace() {
      return this.faces[0] || { url: "https://placehold.co/250" };
    },
    otherFaces() {
      return this.faces.slice(1);
    },
    faces() {
      return this.assets.filter((asset) => asset.type === "FaceImg");
    },
    allFaces() {
      if (!this.firstFace?.url) return [];

      const faces = [{ url: this.firstFace.url }];
      if (this.otherFaces) {
        this.otherFaces.forEach(face => {
          if (face.url) {
            faces.push({ url: face.url })
          }
        });
      }

      return faces;
    },
    evidence() {
      return this.assets.filter((asset) => asset.type !== "FaceImg");
    },
    missingEvidence() {
      return this.evidence.filter(file => file.url === '' || file.url === null);
    },
    closureReasonLength() {
      let reason = this.closure.reason || "";
      // remove white spaces
      reason = reason.replace(/\s/g, "");
      return reason.length;
    },
    readyToUpload() {
      if (this.uploadAssets.files.length === 0) return false;
      for (let file of this.uploadAssets.files) {
        if (!file.rawFile || !file.description) return false;
      }
      return true;
    },
    readyToReupload() {
      // Check if there are files selected for reupload
      if (this.assetReupload.files.length === 0) return false;
      
      // Get the selected file for reupload
      const file = this.assetReupload.files[0];
      
      // Check if the file extension matches the required extension
      if (file.rawFile) {
        const fileExtension = file.rawFile.name.split('.').pop().toLowerCase();
        return fileExtension === this.reuploadExtension;
      }
      
      return false;
    },
    normalizedResubmissionDescription() {
      let description = this.resubmission.description || "";
      return description.replace(/\s+/g, ' ').trim();
    },
    scrollToIncidentNotes() {
      this.$nextTick(() => {
        const incidentNotesSection = this.$refs.incidentNotes;
        if (incidentNotesSection) {
          incidentNotesSection.scrollIntoView({ behavior: 'smooth' });
        }
      });
    },
    showAlertsBanner() {
      const status = this.incident?.status || "";
      return ["Pending", "PendingAsset", "Creating"].includes(status);
    },  
    displayStatus() {
      return this.formattedStatus(this.incident?.status || "");
    },
    formattedStatus() {
      return (status) => {
        switch (status) {
          case "PendingAsset":
            return "Processing";
          case "Pending":
            return "Pending Review";
          case "PendingClosed":
            return "Pending Close Review";
          case "AwaitingEvidence":
            return "Awaiting Evidence";
          case "PendingNational":
            return "Pending National Review";
          case "ApprovedNational":
            return "Approved National";
          case "RejectedNational":
            return "Rejected National";
          default:
            return status;
        }
      };
    },
  },
};
</script>

<style scoped>
label {
  font-weight: bold;
}
/* Critical should flash */
.sev-critical {
  background-color: red;
  width: 10px;
  height: 10px;
  display: inline-block;
  border-radius: 50%;
  animation: flash 1s infinite;
  margin-right: 5px;
}

@keyframes flash {
  0%,
  49% {
    opacity: 1;
  }
  50%,
  100% {
    opacity: 0;
  }
}

.sev-high {
  background-color: red;
  width: 10px;
  height: 10px;
  display: inline-block;
  border-radius: 50%;
  margin-right: 5px;
}

.sev-med {
  background-color: orange;
  width: 10px;
  height: 10px;
  display: inline-block;
  border-radius: 50%;
  margin-right: 5px;
}

.sev-low {
  background-color: green;
  width: 10px;
  height: 10px;
  display: inline-block;
  border-radius: 50%;
  margin-right: 5px;
}

.sev-unknown,
.sev-closed,
.sev-pending-review {
  background-color: grey;
  width: 10px;
  height: 10px;
  display: inline-block;
  border-radius: 50%;
  margin-right: 5px;
}

.timeline {
  display: flex;
  flex-direction: column-reverse;
  list-style: disc;
}

.timeline-time {
  color: rgba(255, 255, 255, 0.6);
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.timeline-action {
  font-style: italic;
}

.wrapper {
  align-items: center;
  display: flex;
  justify-content: center;
  flex-direction: column;
  height: 60vh;
}

.sub-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.note-item {
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 5px;
  border: 1px solid lightgrey;
  max-width: 60%;
}

.own {
  margin-left: auto;
}
.note-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
  font-weight: bold;
}

.note-content {
  white-space: pre-wrap;
  margin-top: 5px;
}

.no-notes {
  text-align: center;
  font-style: italic;
  color: #888;
  padding: 20px;
}

.edit-icon {
  cursor: pointer;
  margin-left: 10px;
}

.tooltip-custom {
  position: absolute;
  background-color: #333;
  color: #fff;
  padding: 5px;
  border-radius: 3px;
  z-index: 1000;
}

.input-no-text {
  border: none;
  background: none;
  padding: 0;
}

.handle {
  cursor: move;
  color: #888;
}

.handle i {
  vertical-align: middle;
}

.text-mute {
  color: #888;
  font-size: 0.9em;
  display: block;
  margin-top: 5px;
}

.table th, .table td {
  vertical-align: middle;
}

.table td span.text-mute {
  display: block;
  margin-top: 5px;
  font-style: italic;
}
.file-type-badge {
  position: absolute;
  top: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 2px 6px;
  font-size: 0.8rem;
  font-weight: bold;
  border-bottom-left-radius: 4px;
  z-index: 2;
}

.evidence-item.needs-reupload {
  cursor: pointer;
  position: relative;
}

.evidence-item.needs-reupload::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.3s;
  z-index: 2;
}

.evidence-item.needs-reupload:hover::before {
  opacity: 1;
}

.centered-upload-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  z-index: 3;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.centered-upload-icon i {
  font-size: 3rem;
}

.needs-reupload {
  border: 2px dashed #dc3545 !important;
  background-color: rgba(220, 53, 69, 0.1);
}

.needs-upload-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: #dc3545;
  color: white;
  padding: 2px 8px;
  font-size: 0.9rem;
  font-weight: bold;
  border-radius: 4px;
  z-index: 3;
  display: flex;
  align-items: center;
}

.needs-upload-badge i {
  font-size: 16px;
  margin-right: 4px;
}
</style>
